// =============================================================================
// AppShell — 全画面共通シェル (2026-07-05 UI刷新 P0)
//
//   「機能別ドロップダウンの寄せ集め」を「業務の7エリア固定左レール」へ刷新。
//   council 6_ux_architect §1 (7エリアIA・26ページ移行マップ) / §2.2 (パレットC)。
//   様式は好評の JournalReviewPage / BatchesPage の複式簿記スタイル
//   (navy #203a57・罫線・monospace数字) をシェルに昇格したもの。
//
//   構成:
//     [左レール 232px 固定 navy]  ロゴ → 7エリア(開閉) → ヘルスミニ表示(60秒poll)
//     [トップバー 52px 白]        エリア > ページ名 パンくず + 日付
//     [HealthBanner]              既存コンポーネントをトップバー直下に維持
//     [コンテンツ]                ground地・レール分の margin。children を render
//
//   レスポンシブ: 1100px 未満でレールを 64px アイコンのみに自動縮小 (label は
//   title tooltip)。ホバー/フォーカスのトランジションは index.css の
//   .fm-rail-* クラスで定義 (inline style では :hover が書けないため)。
// =============================================================================

import React, { useCallback, useEffect, useState } from 'react';
import {
  // エリア見出し
  MdToday,
  MdSyncAlt,
  MdReceiptLong,
  MdRule,
  MdGavel,
  MdAssessment,
  MdTune,
  // シェル部品
  MdExpandMore,
  MdChevronRight,
  // ページアイコン (旧 PAGES 配列から引継ぎ)
  MdDescription,
  MdRestaurantMenu,
  MdAccountBalance,
  MdInbox,
  MdPayments,
  MdSecurity,
  MdEventNote,
  MdRepeat,
  MdAccountBalanceWallet,
  MdMenuBook,
  MdFactCheck,
  MdChecklist,
  MdTableChart,
  MdGridOn,
  MdAutoStories,
  MdSettingsSuggest,
  MdAutoAwesome,
  MdPolicy,
  MdInsights,
  MdPersonOff,
  MdHelp,
  MdReportProblem,
  MdSummarize,
  MdCompareArrows,
  MdEditNote,
  MdTrendingUp,
  MdPlaylistAddCheck,
} from 'react-icons/md';
import { C, FONT, NUM, FS, RADIUS, Z } from '../theme/tokens';
import { fetchHealthPipeline, fetchJobs, PipelineHealth } from '../api/client';
import { HealthBanner } from './HealthBanner';
import AuditBlockDialog from './AuditBlockDialog';

// -----------------------------------------------------------------------------
// ページ定義 (26キー。URL hash = キー名。旧 App.tsx PAGES の後継)
// -----------------------------------------------------------------------------

export type PageKey =
  | 'inbox'
  | 'invoice' | 'invoice_runs' | 'nippou' | 'bank' | 'filebox' | 'payroll' | 'alsok'
  | 'month_end' | 'fixed' | 'market_rent' | 'cashbooks'
  | 'journal_review' | 'batches' | 'payment_aging'
  | 'rulebook' | 'rules' | 'learning'
  | 'findings' | 'diagnosis' | 'partner_unregistered' | 'section_unknown' | 'duplicate_candidates'
  | 'financial' | 'budget_actual' | 'dept_summary' | 'budget_create' | 'breakeven';

type IconCmp = React.ComponentType<any>;

export const PAGE_META: Record<PageKey, { label: string; Icon: IconCmp }> = {
  inbox:                 { label: '受信箱',         Icon: MdToday },
  invoice:               { label: '請求書',         Icon: MdDescription },
  invoice_runs:          { label: '請求書処理run',  Icon: MdPlaylistAddCheck },
  nippou:                { label: '日報',           Icon: MdRestaurantMenu },
  bank:                  { label: '銀行',           Icon: MdAccountBalance },
  filebox:               { label: '証憑',           Icon: MdInbox },
  payroll:               { label: '給与',           Icon: MdPayments },
  alsok:                 { label: 'ALSOK',          Icon: MdSecurity },
  month_end:             { label: '月次仕訳',       Icon: MdEventNote },
  fixed:                 { label: '定額',           Icon: MdRepeat },
  market_rent:           { label: '市場家賃',       Icon: MdAccountBalanceWallet },
  cashbooks:             { label: '出納帳',         Icon: MdMenuBook }, // 西武信金/小口現金/室長立替金 統合 (2026-07-22)
  journal_review:        { label: '仕訳レビュー',   Icon: MdFactCheck },
  batches:               { label: 'バッチ台帳',     Icon: MdChecklist },
  payment_aging:         { label: '支払年齢表',     Icon: MdTableChart },
  rulebook:              { label: 'ルールブック',   Icon: MdAutoStories },
  rules:                 { label: '自動登録ルール', Icon: MdSettingsSuggest },
  learning:              { label: '学習機構',       Icon: MdAutoAwesome },
  findings:              { label: '監査指摘',       Icon: MdPolicy },
  diagnosis:             { label: '診断',           Icon: MdInsights },
  partner_unregistered:  { label: '未登録業者',     Icon: MdPersonOff },
  section_unknown:       { label: '部門不明',       Icon: MdHelp },
  duplicate_candidates:  { label: '重複疑い',       Icon: MdReportProblem },
  financial:             { label: '決算書',         Icon: MdSummarize },
  budget_actual:         { label: '予算実績',       Icon: MdCompareArrows },
  dept_summary:          { label: '部門集計表',     Icon: MdGridOn },
  budget_create:         { label: '予算作成',       Icon: MdEditNote },
  breakeven:             { label: '損益分岐',       Icon: MdTrendingUp },
};

export const ALL_PAGE_KEYS = Object.keys(PAGE_META) as readonly PageKey[];

// -----------------------------------------------------------------------------
// 7エリアIA (council 6_ux_architect §1.1 / §1.3 移行マップ準拠)
// -----------------------------------------------------------------------------

export type AreaKey = 'inbox' | 'flows' | 'journal' | 'rules' | 'audit' | 'reports' | 'settings';

export interface AreaDef {
  key: AreaKey;
  label: string;
  Icon: IconCmp;
  pages: readonly PageKey[]; // 空 = 将来拡張 (設定)
}

export const AREAS: readonly AreaDef[] = [
  { key: 'inbox',    label: '受信箱',   Icon: MdToday,      pages: ['inbox'] },
  { key: 'flows',    label: 'フロー',   Icon: MdSyncAlt,    pages: ['invoice', 'invoice_runs', 'nippou', 'bank', 'filebox', 'payroll', 'alsok', 'month_end', 'fixed', 'market_rent', 'cashbooks'] },
  { key: 'journal',  label: '仕訳',     Icon: MdReceiptLong, pages: ['journal_review', 'batches', 'payment_aging'] },
  { key: 'rules',    label: 'ルール',   Icon: MdRule,       pages: ['rulebook', 'rules', 'learning'] },
  { key: 'audit',    label: '監査',     Icon: MdGavel,      pages: ['findings', 'diagnosis', 'partner_unregistered', 'section_unknown', 'duplicate_candidates'] },
  { key: 'reports',  label: 'レポート', Icon: MdAssessment, pages: ['financial', 'budget_actual', 'dept_summary', 'budget_create', 'breakeven'] },
  { key: 'settings', label: '設定',     Icon: MdTune,       pages: [] },
];

export function areaOfPage(page: PageKey): AreaDef {
  return AREAS.find((a) => a.pages.includes(page)) || AREAS[0];
}

// -----------------------------------------------------------------------------
// ヘルス (60秒poll)。worker /api/health/pipeline は findings 統計も返す
// (client.ts の PipelineHealth 型が未追随のため extend で受ける)
// -----------------------------------------------------------------------------

type ShellHealth = PipelineHealth & { findings?: { open?: number; high?: number } };

// -----------------------------------------------------------------------------
// レール幅・スタイル
// -----------------------------------------------------------------------------

const RAIL_W = 232;
const RAIL_W_NARROW = 64;

const railSt = (railW: number): React.CSSProperties => ({
  position: 'fixed', top: 0, left: 0, bottom: 0, width: railW,
  display: 'flex', flexDirection: 'column',
  background: C.navy, color: '#fff', zIndex: Z.rail,
  fontFamily: FONT, transition: 'width .18s ease',
  boxSizing: 'border-box',
});

const logoSt = (narrow: boolean): React.CSSProperties => ({
  display: 'flex', flexDirection: 'column', alignItems: narrow ? 'center' : 'flex-start',
  padding: narrow ? '14px 0 12px' : '15px 18px 12px',
  borderBottom: '1px solid rgba(255,255,255,.14)',
  color: '#fff', textDecoration: 'none', flex: '0 0 auto',
});

const areaSt = (active: boolean, narrow: boolean, disabled?: boolean): React.CSSProperties => ({
  position: 'relative',
  display: 'flex', alignItems: 'center', gap: 10,
  width: `calc(100% - ${narrow ? 12 : 16}px)`, boxSizing: 'border-box',
  margin: narrow ? '2px 6px' : '2px 8px',
  padding: narrow ? '10px 0' : '8px 11px',
  justifyContent: narrow ? 'center' : 'flex-start',
  border: 0, borderRadius: RADIUS.panel,
  background: active ? C.blueTint : 'transparent',
  color: active ? C.navy : '#cdd9e6',
  font: `800 12.5px ${FONT}`, letterSpacing: '.05em',
  cursor: disabled ? 'default' : 'pointer',
  textAlign: 'left', textDecoration: 'none', whiteSpace: 'nowrap',
  opacity: disabled ? 0.45 : 1,
});

const subSt = (active: boolean, narrow: boolean): React.CSSProperties => ({
  position: 'relative',
  display: 'flex', alignItems: 'center', gap: 8,
  margin: narrow ? '1px 6px' : '1px 8px 1px 14px',
  padding: narrow ? '8px 0' : '6px 10px 6px 21px',
  justifyContent: narrow ? 'center' : 'flex-start',
  borderRadius: RADIUS.panel,
  background: active ? C.blueTint : 'transparent',
  color: active ? C.navy : '#c2d1e0',
  boxShadow: active ? `inset 3px 0 0 ${C.blueLine}` : undefined,
  font: `${active ? 800 : 600} 12px ${FONT}`,
  textDecoration: 'none', whiteSpace: 'nowrap', overflow: 'hidden',
});

const badgeSt = (active: boolean): React.CSSProperties => ({
  marginLeft: 'auto', minWidth: 20, textAlign: 'center',
  padding: '1px 6px', borderRadius: 9,
  fontFamily: NUM, fontSize: 10.5, fontWeight: 900, fontVariantNumeric: 'tabular-nums',
  background: active ? C.navy : '#e8b45a',
  color: active ? '#fff' : C.ink,
});

const narrowBadgeSt: React.CSSProperties = {
  position: 'absolute', top: 4, right: 8,
  minWidth: 16, height: 16, lineHeight: '16px', textAlign: 'center',
  padding: '0 3px', borderRadius: 8,
  fontFamily: NUM, fontSize: 9.5, fontWeight: 900, fontVariantNumeric: 'tabular-nums',
  background: '#e8b45a', color: C.ink,
};

const topbarSt: React.CSSProperties = {
  position: 'sticky', top: 0, zIndex: Z.rail - 10,
  display: 'flex', alignItems: 'center', gap: 8,
  height: 52, padding: '0 20px', boxSizing: 'border-box',
  background: C.surface, borderBottom: `1px solid ${C.line}`,
  fontFamily: FONT, color: C.ink,
};

// -----------------------------------------------------------------------------
// AppShell 本体
// -----------------------------------------------------------------------------

export const AppShell: React.FC<{
  page: PageKey;
  onNavigate: (key: PageKey) => void;
  children?: React.ReactNode;
}> = ({ page, onNavigate, children }) => {
  // -- レスポンシブ (1100px 未満 = アイコンのみ 64px) --
  const [narrow, setNarrow] = useState<boolean>(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 1099px)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1099px)');
    const on = (e: MediaQueryListEvent) => setNarrow(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', on);
    else mq.addListener(on); // Safari 13 系 fallback
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', on);
      else mq.removeListener(on);
    };
  }, []);
  const railW = narrow ? RAIL_W_NARROW : RAIL_W;

  // -- エリア開閉: 現在ページのエリアは自動展開。ユーザー操作は override で保持 --
  const currentArea = areaOfPage(page);
  const [override, setOverride] = useState<Partial<Record<AreaKey, boolean>>>({});
  useEffect(() => {
    // ページ遷移で着地したエリアは必ず展開 (過去に手動で畳んでいても解除)
    setOverride((o) => (o[currentArea.key] === false ? { ...o, [currentArea.key]: true } : o));
  }, [currentArea.key]);
  const isOpen = useCallback(
    (a: AreaDef) => (override[a.key] !== undefined ? !!override[a.key] : a.pages.includes(page)),
    [override, page]
  );
  const toggleArea = (a: AreaDef) => setOverride((o) => ({ ...o, [a.key]: !isOpen(a) }));

  // -- ヘルス 60秒 poll (受信箱バッジ + レール下部ミニ表示) --
  const [health, setHealth] = useState<ShellHealth | null>(null);
  const [deadLetter, setDeadLetter] = useState<number | null>(null);
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      const [rh, rd] = await Promise.allSettled([
        fetchHealthPipeline(),
        fetchJobs({ status: 'dead_letter', limit: 100 }),
      ]);
      if (!alive) return;
      setHealth(rh.status === 'fulfilled' ? (rh.value as ShellHealth) : null);
      setDeadLetter(rd.status === 'fulfilled' ? (rd.value.jobs || []).length : null);
    };
    tick();
    const t = setInterval(tick, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);
  const inboxBadge = health ? (health.batches?.attention || 0) + (health.findings?.open || 0) : 0;

  // -- document.title --
  useEffect(() => {
    document.title = `FREEEMAN — ${PAGE_META[page].label}`;
  }, [page]);

  // -- ナビクリック (左click=SPA遷移 / 修飾キー・中clickはブラウザ既定=別tab) --
  const pick = (key: PageKey) => (e: React.MouseEvent) => {
    if (e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey) {
      e.preventDefault();
      onNavigate(key);
    }
  };

  // -- トップバー日付 --
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const dateStr = `${now.getFullYear()}/${pad(now.getMonth() + 1)}/${pad(now.getDate())}(${'日月火水木金土'[now.getDay()]})`;

  // -- レール下部ヘルス表示素材 --
  const sweepOk = !!health && !health.sweep.stale;
  const sweepDotColor = health ? (sweepOk ? '#57c99b' : '#e57368') : '#8296ab';
  const sweepLabel = health ? (sweepOk ? 'スイープ稼働' : 'スイープ停止') : 'ヘルス取得不可';
  const healthTitle = `照合スイープ: ${sweepLabel}${health?.sweep.last_run_at ? ` (最終 ${new Date(health.sweep.last_run_at).toLocaleString('ja-JP')})` : ''} / dead_letter ${deadLetter === null ? '—' : `${deadLetter}件`}`;

  const showAreaCrumb = currentArea.pages.length > 1; // 受信箱は「受信箱 > 受信箱」にしない

  return (
    <>
      {/* ============================== 左レール ============================== */}
      <nav className="pa-no-print fm-rail" style={railSt(railW)} aria-label="メインナビゲーション">
        <a className="fm-rail-logo" href="#inbox" onClick={pick('inbox')} style={logoSt(narrow)}
           title="FREEEMAN — 経理自動化ハブ">
          {narrow ? (
            <span style={{ fontFamily: NUM, fontSize: 15, fontWeight: 900, letterSpacing: '.04em' }}>FM</span>
          ) : (
            <>
              <span style={{ fontSize: 17, fontWeight: 900, letterSpacing: '.09em' }}>FREEEMAN</span>
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.2em', color: '#8fa6bf', marginTop: 3 }}>経理自動化ハブ</span>
            </>
          )}
        </a>

        <div className="fm-rail-scroll" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '8px 0 10px' }}>
          {AREAS.map((area) => {
            // ① 受信箱: サブ無しの直リンク (openバッジ = attention + findings)
            if (area.key === 'inbox') {
              const active = page === 'inbox';
              return (
                <a key={area.key}
                   className={`fm-rail-area${active ? ' fm-active' : ''}`}
                   href="#inbox" onClick={pick('inbox')}
                   style={areaSt(active, narrow)}
                   title={narrow
                     ? `${area.label}${inboxBadge > 0 ? ` (対応待ち ${inboxBadge}件)` : ''}`
                     : (inboxBadge > 0 ? `対応待ち ${inboxBadge}件 — 要修正バッチ(#batches)+監査確認(#findings)` : undefined)}
                   aria-current={active ? 'page' : undefined}>
                  <area.Icon size={18} style={{ flex: '0 0 auto' }} />
                  {!narrow && <span>{area.label}</span>}
                  {!narrow && inboxBadge > 0 && <span style={badgeSt(active)}>{inboxBadge > 99 ? '99+' : inboxBadge}</span>}
                  {narrow && inboxBadge > 0 && <span style={narrowBadgeSt}>{inboxBadge > 99 ? '99+' : inboxBadge}</span>}
                </a>
              );
            }
            // ⑦ 設定: 将来拡張 (サブ項目なし・押下不能)
            if (area.pages.length === 0) {
              return (
                <div key={area.key}
                     className="fm-rail-area fm-disabled"
                     style={areaSt(false, narrow, true)}
                     title={`${area.label} (準備中)`}
                     aria-disabled="true">
                  <area.Icon size={18} style={{ flex: '0 0 auto' }} />
                  {!narrow && <span>{area.label}</span>}
                  {!narrow && <span style={{ marginLeft: 'auto', fontSize: 9, fontWeight: 800, letterSpacing: '.08em', color: '#8fa6bf' }}>準備中</span>}
                </div>
              );
            }
            // ②〜⑥: 見出し(開閉) + サブ項目
            const open = isOpen(area);
            const containsCurrent = area.pages.includes(page);
            return (
              <div key={area.key}>
                <button type="button"
                        className="fm-rail-area"
                        style={areaSt(false, narrow)}
                        onClick={() => toggleArea(area)}
                        title={narrow ? area.label : undefined}
                        aria-expanded={open}>
                  <area.Icon size={18} style={{ flex: '0 0 auto', color: containsCurrent ? '#fff' : undefined }} />
                  {!narrow && <span style={{ color: containsCurrent ? '#fff' : undefined }}>{area.label}</span>}
                  {!narrow && (
                    <MdExpandMore size={16}
                      style={{ marginLeft: 'auto', opacity: 0.65, transition: 'transform .18s ease', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }} />
                  )}
                </button>
                {open && area.pages.map((pk) => {
                  const meta = PAGE_META[pk];
                  const active = pk === page;
                  return (
                    <a key={pk}
                       className={`fm-rail-sub${active ? ' fm-active' : ''}`}
                       href={`#${pk}`} onClick={pick(pk)}
                       style={subSt(active, narrow)}
                       title={narrow ? meta.label : undefined}
                       aria-current={active ? 'page' : undefined}>
                      <meta.Icon size={narrow ? 17 : 14} style={{ flex: '0 0 auto', opacity: active ? 1 : 0.75 }} />
                      {!narrow && <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{meta.label}</span>}
                    </a>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* レール下部: ヘルスミニ表示 (スイープ死活 + dead_letter 件数) */}
        <div style={{
          flex: '0 0 auto', borderTop: '1px solid rgba(255,255,255,.14)',
          display: 'flex', flexDirection: narrow ? 'column' : 'row', alignItems: 'center',
          gap: narrow ? 5 : 12, padding: narrow ? '10px 4px' : '10px 16px',
          fontFamily: NUM, fontSize: FS.label, fontWeight: 700, color: '#aebfd3',
        }} title={healthTitle}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
            <span aria-hidden style={{ width: 8, height: 8, borderRadius: '50%', background: sweepDotColor, flex: '0 0 auto' }} />
            {!narrow && sweepLabel}
          </span>
          <span style={{
            marginLeft: narrow ? 0 : 'auto', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
            color: deadLetter && deadLetter > 0 ? '#f2a49c' : '#8fa6bf',
          }}>
            {narrow ? (deadLetter === null ? '—' : deadLetter) : `DL ${deadLetter === null ? '—' : deadLetter}`}
          </span>
        </div>
      </nav>

      {/* ============================== 本文側 ============================== */}
      <div className="fm-shell-main" style={{ marginLeft: railW, minHeight: '100vh', background: C.ground, transition: 'margin-left .18s ease' }}>
        <header className="pa-no-print fm-topbar" style={topbarSt}>
          {showAreaCrumb && (
            <>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: C.sub, fontSize: FS.sub, fontWeight: 700 }}>
                <currentArea.Icon size={15} style={{ color: '#8296ab' }} />
                {currentArea.label}
              </span>
              <MdChevronRight size={16} style={{ color: C.lineDark, flex: '0 0 auto' }} aria-hidden />
            </>
          )}
          <h1 style={{ margin: 0, fontSize: FS.head, fontWeight: 800, letterSpacing: '.02em', color: C.ink, whiteSpace: 'nowrap' }}>
            {PAGE_META[page].label}
          </h1>
          <span style={{ marginLeft: 'auto', fontFamily: NUM, fontSize: FS.sub, fontWeight: 700, color: C.sub, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
            {dateStr}
          </span>
        </header>
        {/* 既存 HealthBanner はトップバー直下に既存のまま */}
        <div className="pa-no-print"><HealthBanner /></div>
        {children}
        {/* 2026-07-15 blockfix: session open 409 (audit blocking) 共通ダイアログ (全ページ常駐) */}
        <AuditBlockDialog />
      </div>
    </>
  );
};

export default AppShell;
